import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';

const CODE_LENGTH = 6;

/**
 * 2FA pixel-perfect CTO-FRONT-002.
 * 6 inputs OTP avec auto-tab + backspace navigation.
 */
export const TwoFactorPage = () => {
  const navigate = useNavigate();
  const { verify2FA, challengeToken, isLoading, error, clearError } = useAuthStore();
  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  // Ce useEffect DOIT rester au-dessus du renvoi conditionnel : React exige le
  // même nombre de hooks à chaque rendu. Le challenge disparaît du store dès la
  // validation du code — le rendu suivant passait alors par le Navigate sans
  // exécuter le hook, et React interrompait la page en pleine connexion.
  useEffect(() => { inputsRef.current[0]?.focus(); }, []);

  if (!challengeToken) return <Navigate to="/login" replace />;

  const setDigit = (i: number, val: string) => {
    const v = val.replace(/\D/g, '').slice(0, 1);
    setDigits((prev) => {
      const next = [...prev]; next[i] = v; return next;
    });
    if (v && i < CODE_LENGTH - 1) inputsRef.current[i + 1]?.focus();
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>, i: number) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      inputsRef.current[i - 1]?.focus();
    }
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    clearError();
    const code = digits.join('');
    if (code.length !== CODE_LENGTH) return;
    try {
      await verify2FA(code);
      // SIMG-COMMERCIAL-LEADBETON-2026-001 + ACHATS-2026-002 — chaque rôle
      // atterrit sur son portail dédié.
      const role = useAuthStore.getState().user?.role;
      const landing = role === 'commercial' ? '/commercial'
                    : role === 'acheteur'   ? '/achats'
                    : '/';
      navigate(landing);
    } catch { /* erreur déjà dans store */ }
  };

  return (
    <div
      className="flex min-h-screen items-center justify-center p-4"
      style={{ background: 'var(--bg)' }}
    >
      <div
        className="w-full max-w-md p-10"
        style={{
          background: 'var(--surface)',
          border:     '1px solid var(--bdr)',
          borderRadius: 'var(--r-lg)',
          boxShadow: 'var(--sh-lg)',
        }}
      >
        <div className="mb-8 text-center">
          <img src="/assets/logos/simgroup-logo-officiel.svg" alt="SIM GROUP" className="mx-auto h-14 object-contain" />
        </div>

        <h1 className="font-d text-center" style={{ fontSize: 26, fontWeight: 700, color: 'var(--tx)' }}>
          Vérification
        </h1>
        <p className="mb-7 mt-1 text-center" style={{ fontSize: 13, color: 'var(--tx-3)' }}>
          Entrez le code à 6 chiffres de votre application d'authentification
        </p>

        <form onSubmit={onSubmit} className="space-y-5">
          <div className="flex justify-center gap-2">
            {digits.map((d, i) => (
              <input
                key={i}
                ref={(el) => { inputsRef.current[i] = el; }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                pattern="[0-9]"
                value={d}
                onChange={(e) => setDigit(i, e.target.value)}
                onKeyDown={(e) => onKey(e, i)}
                className="font-d text-center"
                style={{
                  width: 48, height: 56,
                  background: 'var(--surface)',
                  border: '1px solid var(--bdr)',
                  borderRadius: 'var(--r-sm)',
                  fontSize: 24, fontWeight: 700,
                  color: 'var(--tx)',
                  outline: 'none',
                }}
                onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--p)'; e.currentTarget.style.boxShadow = '0 0 0 3px var(--p-soft)'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--bdr)'; e.currentTarget.style.boxShadow = 'none'; }}
              />
            ))}
          </div>

          {error && (
            <div className="rounded-md border px-3 py-2 text-center text-xs"
              style={{ background: '#fef2f2', borderColor: '#fecaca', color: '#7f1d1d' }}>
              {error.message}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading || digits.join('').length !== CODE_LENGTH}
            className="ds-btn ds-btn-p w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ padding: '12px 16px', fontSize: 14 }}
          >
            {isLoading ? 'Validation…' : 'Valider'}
          </button>

          <div className="text-center">
            <a href="#" className="text-xs font-medium hover:underline" style={{ color: 'var(--p)' }}>
              Utiliser un code email à la place
            </a>
          </div>

          <div className="text-center text-xs" style={{ color: 'var(--tx-3) ' }}>
            <button type="button" onClick={() => navigate('/login')} className="hover:underline" style={{ background: 'none', border: 'none' }}>
              ← Retour à la connexion
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
